import React, { useCallback, useEffect, useState } from 'react';
import SendUpdateDialog from '../SendUpdateDialog/SendUpdateDialog';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  VerifiedUser as VerifiedUserIcon,
} from '@mui/icons-material';
import CaseContactApi, { CaseContactDto, CaseContactInput } from '../../api/CaseContactApi';

/**
 * Approved contacts: people the firm may talk to about a client's case who are not the client, such
 * as an OTR case manager (Hannah 7 Oct, Lloyd 9 Oct 2026: "approved related names should count as
 * authorized case contacts").
 *
 * On the client page it lists everyone and lets staff file a contact against one case or all of
 * them. On the case page (`ticketGuid` set) it lists the contacts that apply to that case, and a new
 * contact defaults to this case.
 */
export interface ApprovedContactsPanelProps {
  userGuid: string;
  /** Set on the case page: show only the contacts for this case. */
  ticketGuid?: string;
  /** The client's cases, for the "applies to" choice. */
  cases: { guid: string; label: string }[];
  /**
   * The client's name, for the permission request (Lloyd, 14 Oct 2026). Without it the request
   * button is not shown, since the message has to be addressed to someone.
   */
  clientName?: string;
}

const ALL_CASES = '__all__';

interface FormState {
  name: string;
  role: string;
  organization: string;
  email: string;
  phone: string;
  note: string;
  scope: string;
}

function emptyForm(defaultScope: string): FormState {
  return { name: '', role: '', organization: '', email: '', phone: '', note: '', scope: defaultScope };
}

function formFrom(contact: CaseContactDto): FormState {
  return {
    name: contact.CaseContact_Name,
    role: contact.CaseContact_Role ?? '',
    organization: contact.CaseContact_Organization ?? '',
    email: contact.CaseContact_Email ?? '',
    phone: contact.CaseContact_Phone ?? '',
    note: contact.CaseContact_Note ?? '',
    scope: contact.Ticket_guid ?? ALL_CASES,
  };
}

/**
 * The request itself. It names the person, says why we are asking, and sends the client to the portal,
 * where the consent question is waiting and where they can check their case at the same time.
 */
export function permissionRequestBody(clientName: string, contactName: string): string {
  // clientDisplayName is "Last, First"; a plain "First Last" is handled too.
  const first = clientName.includes(',') ? clientName.split(',')[1].trim() : clientName.split(' ')[0];
  return [
    `Hello ${first || 'there'},`,
    '',
    `${contactName} has asked us about your case. We can only share your case status with people you approve.`,
    '',
    `If you would like us to share it with ${contactName}, please log in to your client portal at ${window.location.origin}/login and answer the question about who may be told about your case. You can check your case there at any time too.`,
    '',
    'If you do not want us to share anything, you do not need to do anything.',
  ].join('\n');
}

const ApprovedContactsPanel: React.FC<ApprovedContactsPanelProps> = ({
  userGuid,
  ticketGuid,
  cases,
  clientName,
}) => {
  const defaultScope = ticketGuid ?? ALL_CASES;
  const [contacts, setContacts] = useState<CaseContactDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null); // guid, or 'new'
  const [form, setForm] = useState<FormState>(emptyForm(defaultScope));
  const [saving, setSaving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  // The contact a permission request is being written about, or null when the dialog is shut.
  const [requestFor, setRequestFor] = useState<CaseContactDto | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setContacts(await CaseContactApi.list(userGuid, ticketGuid));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load the approved contacts');
    } finally {
      setLoading(false);
    }
  }, [userGuid, ticketGuid]);

  useEffect(() => {
    load();
  }, [load]);

  const caseLabel = (guid: string | null): string => {
    if (!guid) return 'All cases';
    return cases.find((c) => c.guid === guid)?.label ?? 'One case';
  };

  const startNew = () => {
    setForm(emptyForm(defaultScope));
    setEditing('new');
    setError(null);
  };

  const startEdit = (contact: CaseContactDto) => {
    setForm(formFrom(contact));
    setEditing(contact.CaseContact_guid);
    setError(null);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    const input: CaseContactInput = {
      name: form.name,
      role: form.role,
      organization: form.organization,
      email: form.email,
      phone: form.phone,
      note: form.note,
      Ticket_guid: form.scope === ALL_CASES ? null : form.scope,
    };
    try {
      if (editing === 'new') await CaseContactApi.create(userGuid, input);
      else if (editing) await CaseContactApi.update(editing, input);
      setEditing(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save the contact');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (guid: string) => {
    setError(null);
    try {
      await CaseContactApi.remove(guid);
      setConfirmRemove(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to remove the contact');
    }
  };

  const set = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
          <VerifiedUserIcon sx={{ mr: 1 }} />
          Approved contacts ({contacts.length})
        </Typography>
        {editing === null && (
          <Button variant="outlined" size="small" onClick={startNew}>
            Add contact
          </Button>
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        People other than the client we may discuss {ticketGuid ? 'this case' : 'these cases'} with,
        such as an OTR case manager.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <CircularProgress size={24} />
      ) : (
        <Stack spacing={1.5}>
          {contacts.length === 0 && editing === null && (
            <Typography variant="body2" color="text.secondary">
              No approved contacts.
            </Typography>
          )}

          {contacts.map((contact) =>
            editing === contact.CaseContact_guid ? null : (
              <Box
                key={contact.CaseContact_guid}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 1,
                  p: 1.5,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {contact.CaseContact_Name}
                    </Typography>
                    {contact.CaseContact_Role && (
                      <Typography variant="body2" color="text.secondary">
                        {contact.CaseContact_Role}
                        {contact.CaseContact_Organization
                          ? `, ${contact.CaseContact_Organization}`
                          : ''}
                      </Typography>
                    )}
                    <Chip size="small" variant="outlined" label={caseLabel(contact.Ticket_guid)} />
                  </Box>
                  <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                    {[contact.CaseContact_Email, contact.CaseContact_Phone]
                      .filter(Boolean)
                      .join(' · ') || 'No email or phone recorded'}
                  </Typography>
                  {contact.CaseContact_Note && (
                    <Typography variant="body2" color="text.secondary">
                      {contact.CaseContact_Note}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    Approved
                    {contact.CaseContact_ApprovedBy ? ` by ${contact.CaseContact_ApprovedBy}` : ''} on{' '}
                    {new Date(contact.CaseContact_CreatedAt).toLocaleDateString()}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexShrink: 0 }}>
                  {confirmRemove === contact.CaseContact_guid ? (
                    <>
                      <Button size="small" color="error" onClick={() => remove(contact.CaseContact_guid)}>
                        Remove
                      </Button>
                      <Button size="small" onClick={() => setConfirmRemove(null)}>
                        Keep
                      </Button>
                    </>
                  ) : (
                    <>
                      {/* Lloyd, 14 Oct 2026: "a button that sends an automatic request to the client
                          asking the client permission to let the third party know ... Perhaps it
                          simply takes them to the portal". Opens Send update with the request
                          written; staff read it and press Send. */}
                      {clientName && (
                        <Button size="small" onClick={() => setRequestFor(contact)}>
                          Request permission
                        </Button>
                      )}
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => startEdit(contact)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove">
                        <IconButton
                          size="small"
                          onClick={() => setConfirmRemove(contact.CaseContact_guid)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>
              </Box>
            ),
          )}

          {editing !== null && (
            <Box sx={{ p: 1.5, border: 1, borderColor: 'primary.main', borderRadius: 1 }}>
              <Stack spacing={1.5}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <TextField
                    label="Name"
                    required
                    size="small"
                    fullWidth
                    value={form.name}
                    onChange={set('name')}
                  />
                  <TextField
                    label="Role"
                    placeholder="OTR case manager"
                    size="small"
                    fullWidth
                    value={form.role}
                    onChange={set('role')}
                  />
                  <TextField
                    label="Organization"
                    size="small"
                    fullWidth
                    value={form.organization}
                    onChange={set('organization')}
                  />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <TextField
                    label="Email"
                    type="email"
                    size="small"
                    fullWidth
                    value={form.email}
                    onChange={set('email')}
                  />
                  <TextField
                    label="Phone"
                    size="small"
                    fullWidth
                    value={form.phone}
                    onChange={set('phone')}
                  />
                  <TextField
                    select
                    label="Applies to"
                    size="small"
                    fullWidth
                    value={form.scope}
                    onChange={set('scope')}
                  >
                    <MenuItem value={ALL_CASES}>All of the client&apos;s cases</MenuItem>
                    {cases.map((c) => (
                      <MenuItem key={c.guid} value={c.guid}>
                        {c.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
                <TextField
                  label="Note"
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  value={form.note}
                  onChange={set('note')}
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={save}
                    disabled={saving || !form.name.trim()}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                  <Button size="small" onClick={() => setEditing(null)} disabled={saving}>
                    Cancel
                  </Button>
                </Box>
              </Stack>
            </Box>
          )}
        </Stack>
      )}
      {clientName && (
        <SendUpdateDialog
          open={requestFor !== null}
          onClose={() => setRequestFor(null)}
          client={{ guid: userGuid, name: clientName }}
          initialSubject="Your permission to share your case status"
          initialBody={requestFor ? permissionRequestBody(clientName, requestFor.CaseContact_Name) : ''}
        />
      )}
    </Paper>
  );
};

export default ApprovedContactsPanel;
